import { domMax, LazyMotion, m, useReducedMotion } from "motion/react";
import * as React from "react";
import { cn } from "../../../lib/utils";
import { Ripple, useRippleState } from "../../ripple";
import { StateLayer } from "../../shared/state-layer";
import { TouchTarget } from "../../shared/touch-target";
import { useSplitButtonContext } from "./split-button-context";
import {
	SPLIT_BUTTON_RADIUS_SPRING,
	SPLIT_BUTTON_TOKENS,
} from "./split-button-tokens";
import type { SplitButtonTrailingUncheckableProps } from "./split-button-types";
import { splitButtonVariants } from "./split-button-variants";

const SplitButtonTrailingUncheckableComponent = React.forwardRef<
	HTMLButtonElement,
	SplitButtonTrailingUncheckableProps
>(
	(
		{
			className,
			style,
			variant = "filled",
			size = "sm",
			icon,
			disabled,
			onClick,
			onPointerDown,
			onPointerUp,
			onPointerLeave,
			onPointerCancel,
			onFocus,
			onBlur,
			"aria-label": ariaLabel,
			...restProps
		},
		ref,
	) => {
		const context = useSplitButtonContext();
		const actualSize = context?.size ?? size;
		const actualVariant = context?.variant ?? variant;

		const [isHovered, setIsHovered] = React.useState(false);
		const [isFocused, setIsFocused] = React.useState(false);
		const [isPressed, setIsPressed] = React.useState(false);

		const tokens = SPLIT_BUTTON_TOKENS[actualSize];
		const prefersReducedMotion = useReducedMotion();
		const needsTouchTarget = actualSize === "xs" || actualSize === "sm";

		const {
			ripples,
			onPointerDown: onRipplePointerDown,
			removeRipple,
		} = useRippleState({ disabled });

		const innerCornerRadius =
			isPressed || isFocused
				? tokens.innerCorner.pressed
				: isHovered
					? tokens.innerCorner.hovered
					: tokens.innerCorner.default;

		const outerRadius =
			(tokens.containerHeight * tokens.outerCornerPercent) / 100;

		// Uncheckable variant always has the asymmetric shape
		const borderRadius = `${innerCornerRadius}px ${outerRadius}px ${outerRadius}px ${innerCornerRadius}px`;

		const handlePointerDown = React.useCallback(
			(e: React.PointerEvent<HTMLButtonElement>) => {
				onRipplePointerDown(e);
				setIsPressed(true);
				onPointerDown?.(e);
			},
			[onRipplePointerDown, onPointerDown],
		);

		const handlePointerUp = React.useCallback(
			(e: React.PointerEvent<HTMLButtonElement>) => {
				setIsPressed(false);
				onPointerUp?.(e);
			},
			[onPointerUp],
		);

		const handlePointerEnter = React.useCallback(() => {
			if (!disabled) {
				setIsHovered(true);
			}
		}, [disabled]);

		const handlePointerLeave = React.useCallback(
			(e: React.PointerEvent<HTMLButtonElement>) => {
				setIsHovered(false);
				setIsPressed(false);
				onPointerLeave?.(e);
			},
			[onPointerLeave],
		);

		const handlePointerCancel = React.useCallback(
			(e: React.PointerEvent<HTMLButtonElement>) => {
				setIsPressed(false);
				onPointerCancel?.(e);
			},
			[onPointerCancel],
		);

		const handleFocus = React.useCallback(
			(e: React.FocusEvent<HTMLButtonElement>) => {
				if (!disabled) setIsFocused(true);
				onFocus?.(e);
			},
			[disabled, onFocus],
		);

		const handleBlur = React.useCallback(
			(e: React.FocusEvent<HTMLButtonElement>) => {
				setIsFocused(false);
				onBlur?.(e);
			},
			[onBlur],
		);

		return (
			<LazyMotion features={domMax} strict>
				<m.button
					ref={ref}
					type="button"
					data-split-button-part="trailing"
					aria-label={ariaLabel}
					disabled={disabled}
					onClick={onClick}
					onPointerDown={handlePointerDown}
					onPointerUp={handlePointerUp}
					onPointerEnter={handlePointerEnter}
					onPointerLeave={handlePointerLeave}
					onPointerCancel={handlePointerCancel}
					onFocus={handleFocus}
					onBlur={handleBlur}
					animate={{ borderRadius }}
					transition={
						prefersReducedMotion ? { duration: 0 } : SPLIT_BUTTON_RADIUS_SPRING
					}
					style={{
						paddingInlineStart: tokens.trailingPadding.start,
						paddingInlineEnd: tokens.trailingPadding.end,
						...style,
					}}
					className={cn(
						splitButtonVariants({
							variant: actualVariant,
							size: actualSize,
							disabled,
						}),
						className,
					)}
					{...restProps}
				>
					{needsTouchTarget && <TouchTarget />}
					<StateLayer
						hovered={isHovered}
						focused={isFocused}
						pressed={isPressed}
					/>
					<Ripple ripples={ripples} onRippleDone={removeRipple} />

					<m.div
						animate={{ x: tokens.opticalCenterOffset }}
						transition={
							prefersReducedMotion
								? { duration: 0 }
								: SPLIT_BUTTON_RADIUS_SPRING
						}
						className="flex items-center justify-center shrink-0 [&>svg]:w-full [&>svg]:h-full"
						style={{
							width: tokens.trailingIconSize,
							height: tokens.trailingIconSize,
						}}
					>
						{icon}
					</m.div>
				</m.button>
			</LazyMotion>
		);
	},
);

SplitButtonTrailingUncheckableComponent.displayName =
	"SplitButtonTrailingUncheckable";

export const SplitButtonTrailingUncheckable = React.memo(
	SplitButtonTrailingUncheckableComponent,
);

// ─────────────────────────────────────────────────────────────────────────────
// Preset Components
// ─────────────────────────────────────────────────────────────────────────────

export const FilledSplitButtonTrailingUncheckable = React.forwardRef<
	HTMLButtonElement,
	Omit<SplitButtonTrailingUncheckableProps, "variant">
>((props, ref) => (
	<SplitButtonTrailingUncheckable ref={ref} variant="filled" {...props} />
));
FilledSplitButtonTrailingUncheckable.displayName =
	"FilledSplitButtonTrailingUncheckable";

export const TonalSplitButtonTrailingUncheckable = React.forwardRef<
	HTMLButtonElement,
	Omit<SplitButtonTrailingUncheckableProps, "variant">
>((props, ref) => (
	<SplitButtonTrailingUncheckable ref={ref} variant="tonal" {...props} />
));
TonalSplitButtonTrailingUncheckable.displayName =
	"TonalSplitButtonTrailingUncheckable";

export const ElevatedSplitButtonTrailingUncheckable = React.forwardRef<
	HTMLButtonElement,
	Omit<SplitButtonTrailingUncheckableProps, "variant">
>((props, ref) => (
	<SplitButtonTrailingUncheckable ref={ref} variant="elevated" {...props} />
));
ElevatedSplitButtonTrailingUncheckable.displayName =
	"ElevatedSplitButtonTrailingUncheckable";

export const OutlinedSplitButtonTrailingUncheckable = React.forwardRef<
	HTMLButtonElement,
	Omit<SplitButtonTrailingUncheckableProps, "variant">
>((props, ref) => (
	<SplitButtonTrailingUncheckable ref={ref} variant="outlined" {...props} />
));
OutlinedSplitButtonTrailingUncheckable.displayName =
	"OutlinedSplitButtonTrailingUncheckable";
